import React, {createContext, useContext, useState, useCallback, useMemo, useEffect, useRef} from 'react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

const ScriptContext = createContext()

// Store to track initial script states before React renders
const initialScriptStates = {}

export const setInitialScriptState = (scriptId, enabled) => {
    initialScriptStates[scriptId] = enabled
}

export const ScriptProvider = ({children}) => {
    const {app: appConfig} = getConfig()
    const [scripts, setScripts] = useState(initialScriptStates)
    const isInitialMount = useRef(true)

    const enableScript = useCallback((scriptId) => {
        setScripts(prev => ({...prev, [scriptId]: true}))
    }, [])

    const disableScript = useCallback((scriptId) => {
        setScripts(prev => ({...prev, [scriptId]: false}))
    }, [])

    const contextValue = useMemo(() => ({
        enableScript,
        disableScript
    }), [enableScript, disableScript])

    // Script configuration
    const scriptConfig = {
        noibu: {
            src: 'https://cdn.noibu.com/collect-core.js',
            id: 'noibu-script',
            globallyEnabled: appConfig.IS_NOIBU_ENABLED
        }
    }

    // Helper function to manage script injection/removal
    const manageScripts = useCallback(() => {
        Object.keys(scriptConfig).forEach(scriptId => {
            const config = scriptConfig[scriptId]
            const pageControl = scripts[scriptId]
            const existingScript = document.getElementById(config.id)

            // Only load if globally enabled AND page hasn't explicitly set it to false
            const shouldLoad = config.globallyEnabled && pageControl !== false

            if (shouldLoad && !existingScript) {
                const script = document.createElement('script')
                script.nonce = window.__CSP_NONCE__
                script.src = config.src
                script.type = 'text/javascript'
                script.id = config.id
                script.async = true
                document.body.appendChild(script)
            } else if (!shouldLoad && existingScript) {
                existingScript.remove()
            }
        })
    }, [scripts])

    // Defer script loading until browser is idle (after React hydration)
    const deferScriptLoading = useCallback((callback) => {
        if (typeof window === 'undefined') return

        // Use requestIdleCallback with a timeout fallback
        if ('requestIdleCallback' in window) {
            window.requestIdleCallback(callback, {timeout: 3000})
        } else {
            // Fallback for Safari - use setTimeout with a small delay
            setTimeout(callback, 1)
        }
    }, [])

    // Dynamically inject/remove scripts
    useEffect(() => {
        // Skip the first render to let pages set their initial state
        if (isInitialMount.current) {
            isInitialMount.current = false
            return
        }

        manageScripts()
    }, [scripts, manageScripts])

    // Load scripts after initial render based on final state
    // Use requestIdleCallback to defer until browser is idle (after React hydration)
    useEffect(() => {
        deferScriptLoading(manageScripts)
    }, [manageScripts, deferScriptLoading])

    return (
        <ScriptContext.Provider value={contextValue}>
            {children}
        </ScriptContext.Provider>
    )
}

export const useScript = () => {
    const context = useContext(ScriptContext)
    if (!context) {
        throw new Error('useScript must be used within a ScriptProvider')
    }
    return context
}
